import { z } from 'zod'

export const ProductCoverageReasonSchema = z.enum([
  'link.no_provider_qualified_alias',
  'match.unknown_persisted_vocabulary',
  'link.unmatched',
  'link.conflict',
  'link.manual_review',
  'mapping.refused',
  'mapping.missing',
  'material.not_authoritative',
  'material.conflicting',
  'weight.authoritative_net_missing',
  'weight.conflicting',
  'price.not_recorded',
  'currency.unsupported',
  'coverage.not_eligible',
])
export type ProductCoverageReason = z.infer<typeof ProductCoverageReasonSchema>

// `GET /api/analytics/products` is still an unmapped flip blocker, so the live
// payload comes from the legacy producer, which emits this instead. Refusing it
// would blank the whole section. Remove this set only after the flip has
// outlived the legacy route's 60-second response cache, not at the flip: until
// it expires the old value is still served, and a build that refuses it shows
// an error instead of a render.
export const LegacyCoverageReasonSchema = z.enum(['legacy_catalog_material_unavailable'])
export const LEGACY_COVERAGE_REASON = LegacyCoverageReasonSchema.enum.legacy_catalog_material_unavailable

export const SoldUnitCoverageReasonSchema = z.union([ProductCoverageReasonSchema, LegacyCoverageReasonSchema])
export type SoldUnitCoverageReason = z.infer<typeof SoldUnitCoverageReasonSchema>

const coverageReasonLabels: Record<SoldUnitCoverageReason, string> = {
  'link.no_provider_qualified_alias': 'Для этого источника не заведена привязка артикула',
  'match.unknown_persisted_vocabulary': 'В сохранённом сопоставлении найдено неизвестное значение',
  'link.unmatched': 'Продажа не сопоставлена с товаром каталога',
  'link.conflict': 'В проверенной привязке продажи найден конфликт',
  'link.manual_review': 'Сопоставление с каталогом ждёт ручной проверки',
  'mapping.refused': 'Импорт 1С отклонил сопоставление этой строки',
  'mapping.missing': 'Для строки продажи нет сопоставления из 1С',
  'material.not_authoritative': 'Металл изделия не подтверждён авторитетным источником',
  'material.conflicting': 'Для изделия принято несколько разных значений металла',
  'weight.authoritative_net_missing': 'Нет авторитетного чистого веса украшения',
  'weight.conflicting': 'Для изделия принято несколько разных значений веса',
  'price.not_recorded': 'Цена продажи не зафиксирована',
  'currency.unsupported': 'Валюта продажи не поддерживается для расчёта',
  'coverage.not_eligible': 'Единица не покрыта: ни одна из известных причин не подошла',
  legacy_catalog_material_unavailable: 'Металл или вес изделия не подтверждены',
}

// The screen never carries an engineering token: an unrecognised code is a
// contract the browser has not been rebuilt for, not something to show.
export function coverageReasonText(reason: string): string {
  const known = SoldUnitCoverageReasonSchema.safeParse(reason)
  return known.success ? coverageReasonLabels[known.data] : 'Причина недоступности требует проверки'
}
